import { typedValues } from '@interxfi/utilities/record/values';
import type {
  DefinedTab,
  DefinedTabGroup,
  DefinedTabOrGroup,
  TabbedNavigationGroups,
  TabbedNavigationTab,
  TabbedNavigationTabType,
} from './router-tabbed-navigation';

interface RouteTabMetadata<GroupId extends string, SubGroupId extends string = never> extends TabbedNavigationTab<GroupId, SubGroupId> {
  routeId: string;
  to: string;
}

export function isDefinedTabGroup(item: DefinedTabOrGroup): item is DefinedTabGroup {
  return 'tabs' in item;
}

function getTabKey(routeId: string, tab: TabbedNavigationTabType<string>): string {
  if ('subGroup' in tab) {
    return tab.subGroup;
  }

  return routeId;
}

export function resolveRouteTabGroups<GroupId extends string>(routes: RouteTabMetadata<GroupId>[]): TabbedNavigationGroups<GroupId> {
  const groups: TabbedNavigationGroups<GroupId> = {};

  const tabsByRouteIdOrSubgroup = new Map(
    routes.map(({ routeId, to, tab }) => [
      getTabKey(routeId, tab),
      {
        routeId,
        to,
        label: tab.label,
        order: tab.order,
      } satisfies DefinedTab,
    ]),
  );

  for (const { routeId, tab } of routes) {
    const currentTab = tabsByRouteIdOrSubgroup.get(getTabKey(routeId, tab))!;

    const group: DefinedTabOrGroup[] = groups[tab.group] ?? [];
    groups[tab.group] = group;

    if ('parentTab' in tab) {
      const parent = tabsByRouteIdOrSubgroup.get(tab.parentTab);
      if (!parent) {
        console.warn(`Tab "${routeId}" references missing parent tab "${tab.parentTab}".`);
        continue;
      }

      let parentGroup = group.find((item): item is DefinedTabGroup => item.routeId === parent.routeId);

      if (!parentGroup) {
        parentGroup = {
          ...parent,
          tabs: [],
        };

        group.push(parentGroup);
      }

      if (!parentGroup.tabs) {
        parentGroup.tabs = [];
      }

      parentGroup.tabs.push(currentTab);

      continue;
    }

    if (!group.some((item) => item.routeId === currentTab.routeId)) {
      group.push(currentTab);
    }
  }

  for (const group of typedValues(groups)) {
    group?.sort((a, b) => a.order - b.order);

    for (const item of group ?? []) {
      if (isDefinedTabGroup(item)) {
        item.tabs.sort((a, b) => a.order - b.order);
      }
    }
  }

  return groups;
}
